import { useState, useCallback } from 'react';
import { useCreateOrganization } from '../../hooks/useOrganizations';
import './CreateOrganizationModal.css';

interface CreateOrganizationModalProps {
  onClose: () => void;
  onSuccess: (orgId: string) => void;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug);
}

export function CreateOrganizationModal({
  onClose,
  onSuccess,
}: CreateOrganizationModalProps) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);

  const createOrg = useCreateOrganization();

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      setName(newName);

      // Auto-generate slug if not manually edited
      if (!slugEdited) {
        setSlug(generateSlug(newName));
        setSlugError(null);
      }
    },
    [slugEdited]
  );

  const handleSlugChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSlug = e.target.value.toLowerCase();
    setSlug(newSlug);
    setSlugEdited(true);

    // Validate slug format
    if (newSlug && !isValidSlug(newSlug)) {
      setSlugError('Slug can only contain lowercase letters, numbers, and hyphens');
    } else {
      setSlugError(null);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !slug.trim()) return;
    if (slugError) return;

    try {
      const org = await createOrg.mutateAsync({ name: name.trim(), slug });
      onSuccess(org.id);
    } catch {
      // Error is handled by the mutation
    }
  };

  const canSubmit =
    name.trim() && slug.trim() && !slugError && !createOrg.isPending;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal__header">
          <h2>Create Organization</h2>
          <button className="modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="modal__body">
            <div className="form-group">
              <label htmlFor="name">Organization Name</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={handleNameChange}
                placeholder="Acme Inc"
                autoFocus
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="slug">
                URL Slug
                <span className="label-hint">
                  Used in URLs (e.g., /organizations/acme-inc)
                </span>
              </label>
              <div className="slug-input">
                <span className="slug-prefix">/</span>
                <input
                  id="slug"
                  type="text"
                  value={slug}
                  onChange={handleSlugChange}
                  placeholder="acme-inc"
                  required
                />
              </div>
              {slugError && <p className="form-error">{slugError}</p>}
            </div>

            {createOrg.isError && (
              <p className="form-error">
                {createOrg.error?.message || 'Failed to create organization'}
              </p>
            )}
          </div>

          <footer className="modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
              disabled={createOrg.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!canSubmit}
            >
              {createOrg.isPending ? 'Creating...' : 'Create Organization'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
